/* eslint-disable jsx-a11y/no-static-element-interactions */
import { DatePicker } from '@components/common';
import { useUpdatePayment } from '@hooks/accommodation';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import {
  ACCOMMODATION_PAYMENT_METHODS,
  AccommodationBooking,
  AccommodationPayment,
} from '../../../../types/accommodation';
import { apiError } from '../../../../util/notify';
import { FinancialEditReasonField } from './FinancialEditReasonField';

const toLocalIsoDate = (value: string) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

interface EditPaymentModalProps {
  isOpen: boolean;
  payment: AccommodationPayment | null;
  booking: AccommodationBooking;
  onClose: () => void;
  onSuccess?: () => void;
}

export const EditPaymentModal: React.FC<EditPaymentModalProps> = ({
  isOpen,
  payment,
  booking,
  onClose,
  onSuccess,
}) => {
  const updatePayment = useUpdatePayment();
  const isSubmitting = updatePayment.isPending;

  const [amount, setAmount] = useState('');
  const [methodId, setMethodId] = useState<number>(1);
  const [notes, setNotes] = useState('');
  const [paidDate, setPaidDate] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const amountRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && payment) {
      setAmount(String(payment.amount));
      setMethodId(payment.payment_method_id);
      setNotes(payment.notes ?? '');
      setPaidDate(toLocalIsoDate(payment.paid_at));
      setReason('');
      setTimeout(() => amountRef.current?.focus(), 80);
    }
  }, [isOpen, payment]);

  if (!isOpen || !payment) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) {
      apiError('Enter a valid payment amount.');
      return;
    }

    const paidAfter = (booking.amount_paid ?? 0) - payment.amount + parsed;
    if (paidAfter > booking.total_amount) {
      apiError(
        `Total payments (${booking.currency} ${paidAfter.toLocaleString()}) would exceed the booking total (${booking.currency} ${booking.total_amount.toLocaleString()}).`,
      );
      return;
    }

    if (!reason.trim()) {
      apiError('Enter a reason for this change.');
      return;
    }

    // Keep the original time of day unless the date itself changed.
    const originalDate = toLocalIsoDate(payment.paid_at);
    const paidAt =
      paidDate && paidDate !== originalDate
        ? new Date(`${paidDate}T12:00:00`).toISOString()
        : payment.paid_at;

    updatePayment.mutate(
      {
        paymentId: payment.id,
        updates: {
          amount: parsed,
          payment_method_id: methodId,
          paid_at: paidAt,
          notes: notes.trim() || null,
        },
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Edit payment
            </h3>
            <p className="text-xs text-gray-400">
              {booking.client
                ? `${booking.client.first_name} ${booking.client.last_name}`
                : `Booking #${booking.id.slice(0, 8).toUpperCase()}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div>
            <label
              htmlFor="ep-amount"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Amount *
            </label>
            <input
              id="ep-amount"
              ref={amountRef}
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <span className="mb-1.5 block text-xs font-medium text-gray-700">
              Payment method *
            </span>
            <div className="flex flex-wrap gap-2">
              {ACCOMMODATION_PAYMENT_METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethodId(m.id)}
                  className={[
                    'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                    methodId === m.id
                      ? 'border-pos-primary bg-pos-primary/10 text-pos-primary'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300',
                  ].join(' ')}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="ep-notes"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Notes (optional)
            </label>
            <input
              id="ep-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reference, receipt number…"
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
            />
          </div>

          <DatePicker
            mode="single"
            id="ep-paid-at"
            label="Payment date *"
            value={paidDate}
            onChange={setPaidDate}
            maxDate={toLocalIsoDate(new Date().toISOString())}
          />

          <FinancialEditReasonField
            id="ep-reason"
            value={reason}
            onChange={setReason}
          />

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !reason.trim()}
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSubmitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
